import React, { ReactNode } from 'react';
import { Box, Button, Chip, IconButton, styled, Tabs, Typography } from '@mui/material';
import { ArrowBack, CloudDoneOutlined } from '@mui/icons-material';
import { useApp } from 'hooks/app';

export const palette = {
  ink: '#1f1723',
  muted: '#6d6470',
  border: '#eadde1',
  primary: '#7b1235',
  primaryDark: '#4a0e1a',
  soft: '#fff8f5',
};

export const PageShell = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '2px 0 16px',
  background:
    'linear-gradient(180deg, rgba(255,248,245,0.55) 0%, rgba(255,255,255,0) 150px), linear-gradient(90deg, rgba(123,18,53,0.03), rgba(251,233,156,0.05))',
  borderRadius: 8,
  [theme.breakpoints.down('sm')]: {
    margin: '0 -10px',
    borderRadius: 0,
  },
}));

export const Hero = styled('section')(({ theme }) => ({
  border: `1px solid ${palette.border}`,
  borderRadius: 8,
  padding: '10px 14px',
  background: '#fff',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: 12,
  alignItems: 'center',
  boxShadow: '0 8px 22px rgba(74, 14, 26, 0.06)',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
  [theme.breakpoints.down('sm')]: {
    borderLeft: 0,
    borderRight: 0,
    borderRadius: 0,
  },
}));

export const HeroValue = styled('div')(({ theme }) => ({
  border: '1px solid rgba(123,18,53,0.18)',
  background: palette.soft,
  borderRadius: 8,
  padding: '8px 12px',
  minWidth: 170,
  textAlign: 'right',
  [theme.breakpoints.down('md')]: {
    minWidth: 'unset',
    textAlign: 'left',
  },
}));

export const CompactMeta = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexWrap: 'wrap',
});

export const MiniMetric = styled('span')({
  border: `1px solid ${palette.border}`,
  borderRadius: 999,
  background: '#fff',
  padding: '3px 8px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  color: palette.primary,
  fontSize: 12,
  fontWeight: 700,
});

export const WorkArea = styled('section')(({ theme }) => ({
  border: `1px solid ${palette.border}`,
  borderRadius: 8,
  background: '#fff',
  overflow: 'hidden',
  boxShadow: '0 10px 24px rgba(74, 14, 26, 0.05)',
  [theme.breakpoints.down('sm')]: {
    borderLeft: 0,
    borderRight: 0,
    borderRadius: 0,
  },
}));

export const ContentPad = styled('div')(({ theme }) => ({
  padding: 12,
  [theme.breakpoints.down('sm')]: {
    padding: 10,
  },
}));

export const StyledForm = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

// Barra de ações fixa no rodapé da tela: fica sempre visível, sem rolar até o fim do formulário.
export const RecordFooter = styled('div')(({ theme }) => ({
  position: 'sticky',
  bottom: 0,
  zIndex: 2,
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 1.75),
  background: '#fff',
  border: `1px solid ${palette.border}`,
  borderRadius: 8,
  boxShadow: '0 -6px 18px rgba(74, 14, 26, 0.06)',
  [theme.breakpoints.down('sm')]: {
    borderLeft: 0,
    borderRight: 0,
    borderRadius: 0,
  },
}));

interface RecordHeaderProps {
  title: string;
  backAction(): void;
  active?: boolean;
  // chips e indicadores extras ao lado do status
  children?: ReactNode;
}

// Cabeçalho do registro: voltar, nome (ou "Novo ...") e status. É o único título da tela além da Appbar.
export const RecordHeader: React.FC<RecordHeaderProps> = ({ title, backAction, active, children }) => {
  const app = useApp();
  // no celular a Appbar já mostra a seta de voltar
  const appbarHasBack = app.isMobile && app.windowWidth < 960;

  return (
    <Hero style={{ gridTemplateColumns: '1fr' }}>
      <CompactMeta>
        {!appbarHasBack && (
          <IconButton size="small" aria-label="Voltar" onClick={backAction} sx={{ mr: 0.5 }}>
            <ArrowBack fontSize="small" />
          </IconButton>
        )}
        <Typography variant="subtitle1" fontWeight={700} color="primary">
          {title}
        </Typography>
        {active !== undefined && (
          <Chip size="small" color={active ? 'success' : 'default'} label={active ? 'Ativo' : 'Inativo'} />
        )}
        {children}
      </CompactMeta>
    </Hero>
  );
};

interface RecordTabsProps {
  tab: string;
  onChange(tab: string): void;
  // as abas (<Tab value label />) da tela
  children: ReactNode;
}

// Barra de abas padrão das telas de cadastro.
export const RecordTabs: React.FC<RecordTabsProps> = ({ tab, onChange, children }) => (
  <Box sx={{ px: 2, pt: 1.5, borderBottom: 1, borderColor: palette.border, background: '#fff' }}>
    <Tabs
      variant="scrollable"
      value={tab}
      onChange={(_, newValue) => onChange(newValue)}
      sx={{
        minHeight: 42,
        '& .MuiTab-root': {
          minHeight: 42,
          borderRadius: '8px 8px 0 0',
          fontWeight: 700,
          textTransform: 'none',
        },
        '& .Mui-selected': {
          backgroundColor: palette.soft,
          color: palette.primary,
        },
      }}
    >
      {children}
    </Tabs>
  </Box>
);

interface CountLabelProps {
  label: string;
  count?: number;
}

// Rótulo de aba com a quantidade de itens ao lado ("Médicos 1").
export const CountLabel: React.FC<CountLabelProps> = ({ label, count }) => (
  <span style={{ whiteSpace: 'nowrap' }}>
    {label}
    {count !== undefined && (
      <Typography component="span" variant="caption" sx={{ ml: 0.75, color: 'text.secondary', fontWeight: 400 }}>
        {count}
      </Typography>
    )}
  </span>
);

interface RecordSectionProps {
  title: string;
  children: ReactNode;
}

// Seção dentro de uma aba (ex.: Anestesista na aba Geral).
export const RecordSection: React.FC<RecordSectionProps> = ({ title, children }) => (
  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${palette.border}` }}>
    <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

// Aviso discreto de como a aba grava (ex.: "salvo na hora").
export const RecordNote: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
    <CloudDoneOutlined sx={{ fontSize: 16 }} />
    {children}
  </Typography>
);

interface RecordActionsProps {
  saving: boolean;
  saveLabel: string;
  onSave(): void;
  // há alterações ainda não salvas
  dirty?: boolean;
}

// Conteúdo padrão da barra de ações: aviso de alterações, Cancelar e Salvar.
export const RecordActions: React.FC<RecordActionsProps> = ({ saving, saveLabel, onSave, dirty }) => (
  <RecordFooter sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography variant="caption" color="textSecondary">
      {dirty ? 'Alterações não salvas' : ''}
    </Typography>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button variant="contained" size="small" disabled={saving} onClick={onSave}>
        {saveLabel}
      </Button>
    </Box>
  </RecordFooter>
);
